// src/screens/FounderStoreScreen.js
// ============================================================================
// FOUNDER MEMBER STORE — Launch Catalog
//
// Displays the Founders 128 Collection + the broader merch catalog. Replaces
// GameStoreScreen on the "Store" route until merch fulfillment is broadly
// integrated. GameStoreScreen.js remains in place for future restoration when
// the merchant-facing game store opens.
//
// Catalog (as of 2026-05-28):
//   - Founder Tee · First Blood Edition  $50  Limited 128 (Phase 3 — code-gated)
//   - Classic CritCash Tee               $25  Uncapped (S–5XL)  ← Phase 1 LIVE
//   - CritCash Utility Backpack          $60  Uncapped (Phase 2)
//   - King Crit Mouse Pad                $30  Uncapped (36×18 desk mat only, 18×16 retired 2026-07-03) (Phase 2)
//   - CritCash Hat                       $20  Uncapped (one-size) (Phase 2)
//   - King Crit Coffee Mug               $13  Uncapped (11 oz / 15 oz) (Phase 2)
//
// Phase 1–2 (2026-05-28): Classic Tee, Backpack, Hat, Mouse Pad, Coffee Mug
// are all wired end-to-end → createMerchCheckoutSession Cloud Function →
// Stripe Checkout → webhook fires Printful order. Single-variant products
// (Backpack, Hat) skip the size modal and go direct to checkout. Founder Tee
// remains "coming soon" pending the Phase 3 invite-code gate.
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  Image,
  ScrollView,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Alert,
  Platform,
  ActivityIndicator,
  Modal,
  TextInput,
} from 'react-native';
import { Ionicons, FontAwesome5 } from '@expo/vector-icons';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import MerchSizeSelector from '../components/MerchSizeSelector';
import { useCart } from '../context/CartContext';

// ----------------------------------------------------------------------------
// Size lists for products with variants. Server-side source of truth is the
// MERCH_CATALOG constant in functions/payments/stripeMerch.js — these arrays
// must stay in sync with the variant keys defined there.
// ----------------------------------------------------------------------------

const CLASSIC_TEE_SIZES = [
  { key: 'S',   label: 'S' },
  { key: 'M',   label: 'M' },
  { key: 'L',   label: 'L' },
  { key: 'XL',  label: 'XL' },
  { key: '2XL', label: '2XL' },
  { key: '3XL', label: '3XL' },
  { key: '4XL', label: '4XL' },
  { key: '5XL', label: '5XL' },
];

const FOUNDER_TEE_SIZES = [
  { key: 'S',   label: 'S' },
  { key: 'M',   label: 'M' },
  { key: 'L',   label: 'L' },
  { key: 'XL',  label: 'XL' },
  { key: '2XL', label: '2XL' },
];

// MerchSizeSelector reads optional `priceCents` per size so the
// "Continue to Checkout — $X" button shows the right price as the buyer
// switches between sizes (Coffee Mug uses this; other products' sizes omit
// priceCents and fall back to the modal's priceCents prop).
// Mouse Pad: 18×16 retired 2026-07-03 (founder) — desk mat only.
const MOUSE_PAD_SIZES = [
  { key: '36x18', label: '36″×18″', priceCents: 3000 },
];

// Coffee Mug is multi-priced — 11 oz is $20, 15 oz is $25 (post-2026-05-30
// pricing audit). MerchSizeSelector reads per-variant priceCents so the
// "Continue to Checkout — $X" button updates as the buyer switches sizes.
// Backend MERCH_CATALOG mirrors these retailCents values via variant-level
// overrides (the 15 oz override wins against the product-level default).
const COFFEE_MUG_SIZES = [
  { key: '11oz', label: '11 oz', priceCents: 2000 },
  { key: '15oz', label: '15 oz', priceCents: 2500 },
];

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

const showAlert = (title, message) => {
  if (Platform.OS === 'web') {
    // eslint-disable-next-line no-alert
    alert(`${title}\n\n${message}`);
  } else {
    Alert.alert(title, message);
  }
};

// ----------------------------------------------------------------------------
// Branded image components — layer MerchBackground.webp behind the
// transparent product PNGs (so the model + tee read against a CritCash
// branded backdrop instead of dropping out against the dark card). Only the
// Classic Tee + Founder Tee use these; accessory products keep their
// standalone product shots.
// ----------------------------------------------------------------------------

const MERCH_BG = require('../../assets/MerchBackground.webp');

// The source product PNGs from Printful are 2000×2000 with the model
// centered and transparent padding all around — leaving a visible "gap"
// above the head and below the feet when shown contained in a portrait
// frame. resizeMode="cover" + a 1.25× transform scale crops the padding so
// the model fills the frame; transformOrigin: 'center bottom' anchors the
// scaling at the bottom so the model's legs land at the bottom border the
// way the founder asked for.
function BrandedHero({ source }) {
  return (
    <View style={styles.brandedHero}>
      <Image source={MERCH_BG} style={styles.brandedHeroBg} resizeMode="cover" />
      <Image source={source} style={styles.brandedHeroFg} resizeMode="cover" />
    </View>
  );
}

function BrandedGalleryCell({ source }) {
  return (
    <View style={styles.brandedGalleryCell}>
      <Image source={MERCH_BG} style={styles.brandedHeroBg} resizeMode="cover" />
      <Image source={source} style={styles.brandedHeroFg} resizeMode="cover" />
    </View>
  );
}

// ----------------------------------------------------------------------------
// Section header — divides the store into category groups (Apparel /
// Accessories). Category is also tracked server-side on MERCH_CATALOG.{key}.
// category; when the catalog outgrows a single scroll we'll lift each section
// into its own route without changing the data model.
// ----------------------------------------------------------------------------

function SectionHeader({ eyebrow, title, subtitle }) {
  return (
    <View style={styles.sectionHeader}>
      <Text style={styles.sectionEyebrow}>{eyebrow}</Text>
      <Text style={styles.sectionTitle}>{title}</Text>
      <View style={styles.sectionDivider} />
      {subtitle ? <Text style={styles.sectionSubtitle}>{subtitle}</Text> : null}
    </View>
  );
}

// ----------------------------------------------------------------------------
// Founder Code Modal — single-input prompt for redeeming the CRITCASH-FOUNDER
// invite code. On a successful redeem the parent re-fetches getFounderTeeStatus
// (which now returns unlocked: true) and swaps the Founder Tee Buy button.
// ----------------------------------------------------------------------------

function FounderCodeModal({ visible, onClose, onRedeem }) {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (visible) {
      setCode('');
      setError('');
      setSubmitting(false);
    }
  }, [visible]);

  const handleSubmit = async () => {
    if (!code.trim() || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await onRedeem(code.trim());
      // parent closes the modal on success
    } catch (err) {
      console.error('Founder code redeem failed:', err);
      const msg = err?.message || 'Could not redeem that code.';
      setError(
        msg.includes('unauthenticated')
          ? 'You need to be signed in to redeem a code.'
          : msg.includes('valid')
            ? "That code isn't valid. Double-check the spelling."
            : msg
      );
      setSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={() => { if (!submitting) onClose(); }}
    >
      <View style={styles.modalBackdrop}>
        <View style={styles.codeModal}>
          <View style={styles.codeModalHeader}>
            <View style={{ flex: 1 }}>
              <Text style={styles.codeModalEyebrow}>FOUNDER ACCESS</Text>
              <Text style={styles.codeModalTitle}>Enter your code</Text>
            </View>
            {!submitting && (
              <TouchableOpacity onPress={onClose} hitSlop={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                <Ionicons name="close" size={24} color="#999" />
              </TouchableOpacity>
            )}
          </View>

          <Text style={styles.codeModalBody}>
            The Founder Tee is reserved for the first 128 code-holders.
            Enter the code you were given to unlock checkout.
          </Text>

          <TextInput
            value={code}
            onChangeText={(v) => { setCode(v); setError(''); }}
            placeholder="CRITCASH-FOUNDER"
            placeholderTextColor="#555"
            autoCapitalize="characters"
            autoCorrect={false}
            editable={!submitting}
            style={styles.codeModalInput}
            onSubmitEditing={handleSubmit}
            returnKeyType="go"
          />

          {error ? <Text style={styles.codeModalError}>{error}</Text> : null}

          <TouchableOpacity
            style={[styles.confirmButton, (!code.trim() || submitting) && styles.confirmButtonDisabled]}
            onPress={handleSubmit}
            disabled={!code.trim() || submitting}
          >
            {submitting ? (
              <ActivityIndicator color="#0A0A0A" />
            ) : (
              <Text style={styles.confirmButtonText}>Unlock</Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

/**
 * Direct-to-checkout handler for single-variant products (Backpack, Hat).
 * Skips the size modal — there's nothing for the user to choose. Calls the
 * Cloud Function and redirects to Stripe Checkout in the same flow the size
 * modal uses internally.
 */
async function startDirectCheckout({ productKey, size = 'OS' }) {
  const functions = getFunctions();
  const createCheckout = httpsCallable(functions, 'createMerchCheckoutSession');
  const result = await createCheckout({ productKey, size, quantity: 1 });
  const { url } = result.data || {};
  if (!url) throw new Error('No checkout URL returned from server.');
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.location.href = url;
  } else {
    showAlert('Checkout URL', url);
  }
}

// ----------------------------------------------------------------------------
// Standard product card (single hero image, used for accessories)
//
// onBuy: async () => void — opens a size modal or triggers direct checkout.
// buyLabel defaults to a Buy Now button using the listed price.
// loading: if true, replaces button text with a spinner (used for direct
// checkout where we redirect to Stripe and don't want a double-tap).
// ----------------------------------------------------------------------------

function StandardProductCard({
  title,
  price,
  priceLabel,    // optional override for the displayed price (e.g. "From $20")
  image,
  description,
  sizeNote,
  onBuy,
  onAddToCart,   // optional — when set, renders an "Add to Cart" secondary
                 // button below the main Buy Now CTA. Pre-confirmed text
                 // flips to "Added ✓" for ~1.5s after a successful add.
  loading = false,
  buyLabel,
}) {
  const [added, setAdded] = useState(false);
  const handleAdd = () => {
    if (!onAddToCart) return;
    onAddToCart();
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };
  return (
    <View style={styles.productCard}>
      <Image source={image} style={styles.standardHeroImage} resizeMode="contain" />
      <Text style={styles.productTitle}>{title}</Text>
      <View style={styles.priceRow}>
        <Text style={styles.productPrice}>{priceLabel || `$${price}`}</Text>
        {sizeNote ? <Text style={styles.productPriceSub}>{sizeNote}</Text> : null}
      </View>
      <Text style={styles.productDescription}>{description}</Text>
      <TouchableOpacity
        style={[styles.buyButton, loading && styles.buyButtonDisabled]}
        onPress={onBuy}
        disabled={loading || !onBuy}
      >
        {loading ? (
          <ActivityIndicator color="#0A0A0A" />
        ) : (
          <Text style={styles.buyButtonText}>
            {buyLabel || (priceLabel ? `Buy Now — ${priceLabel}` : `Buy Now — $${price}`)}
          </Text>
        )}
      </TouchableOpacity>
      {onAddToCart ? (
        <TouchableOpacity
          style={[styles.addCartButtonSecondary, added && styles.addCartButtonAdded]}
          onPress={handleAdd}
          disabled={loading}
        >
          <Text style={[styles.addCartButtonSecondaryText, added && styles.addCartButtonAddedText]}>
            {added ? 'Added ✓' : 'Add to Cart'}
          </Text>
        </TouchableOpacity>
      ) : null}
    </View>
  );
}

// ----------------------------------------------------------------------------
// Main screen
// ----------------------------------------------------------------------------

export default function FounderStoreScreen({ navigation }) {
  const [classicTeeModalOpen, setClassicTeeModalOpen] = useState(false);
  const [mousePadModalOpen, setMousePadModalOpen] = useState(false);
  const [coffeeMugModalOpen, setCoffeeMugModalOpen] = useState(false);
  const [founderTeeModalOpen, setFounderTeeModalOpen] = useState(false);
  const [founderCodeModalOpen, setFounderCodeModalOpen] = useState(false);

  // Founder Tee status: { unlocked, remaining, cap, soldOut, comingSoon,
  // alreadyRedeemed, isAdmin }. Fetched on mount; refreshed after a
  // successful code redemption.
  const [founderStatus, setFounderStatus] = useState(null);
  const [founderStatusLoading, setFounderStatusLoading] = useState(true);

  // Track which single-variant product is mid-checkout so we can show a
  // spinner on its button. We disable both during one checkout to prevent the
  // user from triggering two Stripe redirects at once.
  const [directCheckoutInFlight, setDirectCheckoutInFlight] = useState(null);

  // Cart context — addItem powers every "Add to Cart" path on this screen.
  // Cart is device-local so guests can build a cart freely; the actual
  // checkout still requires sign-in (gated at MerchCheckoutScreen).
  const { addItem: addToCart } = useCart();

  const fetchFounderStatus = useCallback(async () => {
    try {
      const functions = getFunctions();
      const getStatus = httpsCallable(functions, 'getFounderTeeStatus');
      const result = await getStatus({});
      setFounderStatus(result.data || null);
    } catch (err) {
      console.error('Failed to fetch Founder Tee status:', err);
      // Defensive fallback: if the status endpoint is unreachable, show the
      // safest possible state ("Checkout Coming Soon") rather than letting
      // the chain fall through to the code-entry CTA. A buyer who entered
      // a code against a broken backend would just hit another error.
      setFounderStatus({ comingSoon: true });
    } finally {
      setFounderStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFounderStatus();
  }, [fetchFounderStatus]);

  // Guest gate: any merch action (Founder code redemption, single-variant
  // direct checkout, multi-variant size modal) routes a signed-out user to
  // SignUp instead of letting them hit the cloud function and bounce off an
  // 'unauthenticated' error. We want to convert curious browsers into
  // accounts at the moment of intent. Added 2026-05-30 pre-launch guest UX
  // audit (multi-variant products already gate at MerchCheckoutScreen, but
  // direct-checkout single-variant + the Founder code modal both skipped
  // that gate).
  const requireSignedInForPurchase = (action = 'continue') => {
    const auth = getAuth();
    if (!auth.currentUser) {
      const handler = Platform.OS === 'web'
        ? () => {
            // eslint-disable-next-line no-alert
            if (confirm(`Create an account to ${action}. Sign up?`)) {
              navigation.navigate('SignUp');
            }
          }
        : () => {
            Alert.alert(
              'Create an account',
              `Sign up to ${action}.`,
              [
                { text: 'Cancel', style: 'cancel' },
                { text: 'Sign Up', onPress: () => navigation.navigate('SignUp') },
              ]
            );
          };
      handler();
      return false;
    }
    return true;
  };

  const openFounderCodeModal = () => {
    if (!requireSignedInForPurchase('redeem your Founder code')) return;
    setFounderCodeModalOpen(true);
  };

  const openClassicTeeModal = () => {
    if (!requireSignedInForPurchase('buy a Classic Tee')) return;
    setClassicTeeModalOpen(true);
  };

  const openMousePadModal = () => {
    if (!requireSignedInForPurchase('buy a King Crit Mouse Pad')) return;
    setMousePadModalOpen(true);
  };

  const openCoffeeMugModal = () => {
    if (!requireSignedInForPurchase('buy a King Crit Coffee Mug')) return;
    setCoffeeMugModalOpen(true);
  };

  const openFounderTeeModal = () => {
    if (!requireSignedInForPurchase('buy the Founder Tee')) return;
    setFounderTeeModalOpen(true);
  };

  const handleRedeemFounderCode = async (code) => {
    const functions = getFunctions();
    const redeem = httpsCallable(functions, 'redeemFounderCode');
    await redeem({ code });
    setFounderCodeModalOpen(false);
    // Re-fetch so the button flips from "Enter code" → "Buy Now"
    await fetchFounderStatus();
  };

  const handleDirectCheckout = async (productKey) => {
    if (directCheckoutInFlight) return;
    // Route a signed-out user to SignUp BEFORE we hit the cloud function.
    // Pre-fix this would call createMerchCheckoutSession, get back
    // 'unauthenticated', and surface a generic alert — bad UX and we lost
    // the convert opportunity.
    if (!requireSignedInForPurchase(`buy the ${productKey === 'backpack' ? 'King Crit Backpack' : 'King Crit Hat'}`)) return;
    setDirectCheckoutInFlight(productKey);
    try {
      await startDirectCheckout({ productKey });
      // On web, startDirectCheckout sets window.location.href — page leaves
      // and the spinner state is moot. We still reset on the error path below.
    } catch (err) {
      console.error('Direct checkout error:', err);
      setDirectCheckoutInFlight(null);
      const friendlyMessage =
        err?.message?.includes('unauthenticated')
          ? 'You need to be signed in to buy merch. Sign in and try again.'
          : `Could not start checkout: ${err.message || 'unknown error'}.`;
      showAlert('Checkout failed', friendlyMessage);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <Header navigation={navigation} />
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {/* Hero */}
        <View style={styles.hero}>
          <Text style={styles.eyebrow}>FIRST BLOOD EDITION</Text>
          <Text style={styles.heroTitle}>The Founders 128 Collection</Text>
          <Text style={styles.heroSubtitle}>
            Wear the founding. Limited launch run, never reprinted.
          </Text>
        </View>

        {/* =============================================================== */}
        {/* FOUNDER TEE — featured, gallery, code-gated (Phase 3) */}
        {/* =============================================================== */}
        <View style={[styles.productCard, styles.productCardFeatured]}>
          <View style={styles.productBadge}>
            <FontAwesome5 name="crown" size={12} color="#0A0A0A" />
            <Text style={styles.productBadgeText}>LIMITED · 128 ONLY</Text>
          </View>

          <BrandedHero source={require('../../assets/FounderTee_Back.webp')} />

          <View style={styles.productGallery}>
            <BrandedGalleryCell source={require('../../assets/FounderTee_Front.webp')} />
            <BrandedGalleryCell source={require('../../assets/FounderTee_Sleeve.webp')} />
          </View>

          <Text style={styles.productTitle}>Founder Tee · First Blood Edition</Text>
          <View style={styles.priceRow}>
            <Text style={styles.productPrice}>$50</Text>
            <Text style={styles.productPriceSub}>Sizes S–2XL</Text>
          </View>

          <Text style={styles.productDescription}>
            Premium denim base — heavier weight than standard cotton. Limited
            to 128 ever made: once the First Blood Edition sells out, it's
            retired permanently — no restocks, no second drop.
          </Text>

          <View style={styles.includedList}>
            <Text style={styles.includedTitle}>What you get with your Founder Tee:</Text>
            <View style={styles.includedRow}>
              <Ionicons name="checkmark-circle" size={18} color="#FCD000" />
              <Text style={styles.includedText}>
                Founding Member status — 1 of only 128, forever
              </Text>
            </View>
            <View style={styles.includedRow}>
              <Ionicons name="checkmark-circle" size={18} color="#FCD000" />
              <Text style={styles.includedText}>
                Founding Member badge on profile (permanent)
              </Text>
            </View>
            <View style={styles.includedRow}>
              <Ionicons name="checkmark-circle" size={18} color="#FCD000" />
              <Text style={styles.includedText}>
                Direct founder access in Discord for the first 90 days
              </Text>
            </View>
            <View style={styles.includedRow}>
              <Ionicons name="checkmark-circle" size={18} color="#FCD000" />
              <Text style={styles.includedText}>
                Founders Wall listing at critcash.com/founders
              </Text>
            </View>
          </View>

          {(() => {
            // Loading state — show a spinner where the button will be.
            if (founderStatusLoading) {
              return (
                <View style={[styles.buyButton, styles.buyButtonDisabled]}>
                  <ActivityIndicator color="#0A0A0A" />
                </View>
              );
            }

            // Catalog not yet activated (numeric sync_variant_ids pending).
            // Shows the original copy until the admin populates IDs.
            if (founderStatus?.comingSoon) {
              return (
                <TouchableOpacity
                  style={[styles.buyButton, styles.buyButtonDisabled]}
                  onPress={() =>
                    showAlert(
                      'Almost ready',
                      'Founder Tee checkout opens as soon as the Printful SKUs are wired in. Your slot is reserved.'
                    )
                  }
                >
                  <Text style={styles.buyButtonText}>Checkout Coming Soon</Text>
                </TouchableOpacity>
              );
            }

            // All claimed.
            if (founderStatus?.soldOut) {
              return (
                <View style={[styles.buyButton, styles.buyButtonSoldOut]}>
                  <Text style={styles.buyButtonText}>
                    Sold Out — All {founderStatus.cap} Claimed
                  </Text>
                </View>
              );
            }

            // Admin or code-redeemed → buy.
            if (founderStatus?.unlocked) {
              return (
                <>
                  <TouchableOpacity
                    style={styles.buyButton}
                    onPress={openFounderTeeModal}
                  >
                    <Text style={styles.buyButtonText}>Buy Now — $50</Text>
                  </TouchableOpacity>
                  {/* Add to Cart only renders for unlocked Founder Tee —
                      the gate intentionally prevents guests from adding a
                      gated SKU to their cart and hitting a server-side
                      "requires code" error at checkout. */}
                  <TouchableOpacity
                    style={styles.addCartButtonSecondary}
                    onPress={openFounderTeeModal}
                  >
                    <Text style={styles.addCartButtonSecondaryText}>Add to Cart</Text>
                  </TouchableOpacity>
                </>
              );
            }

            // Locked — surface the code-entry flow.
            return (
              <TouchableOpacity
                style={styles.buyButton}
                onPress={openFounderCodeModal}
              >
                <Text style={styles.buyButtonText}>Enter Founder Code</Text>
              </TouchableOpacity>
            );
          })()}

          {/* Live counter — visible to everyone, including signed-out. Drives
              urgency the same way the Printful dashboard's count does for the
              founder. */}
          {founderStatus && !founderStatus.comingSoon ? (
            <Text
              style={[
                styles.codeNote,
                founderStatus.soldOut && { color: '#FF6B6B' },
              ]}
            >
              {founderStatus.soldOut
                ? `All ${founderStatus.cap} Founder Tees have been claimed.`
                : `${founderStatus.remaining} of ${founderStatus.cap} remaining`}
              {founderStatus.alreadyRedeemed ? ' · ✓ Your code is redeemed' : ''}
            </Text>
          ) : null}

          {/* Code requirement note — only relevant when the buyer hasn't yet
              redeemed and the product isn't sold out / coming soon. */}
          {founderStatus &&
           !founderStatus.unlocked &&
           !founderStatus.soldOut &&
           !founderStatus.comingSoon ? (
            <Text style={styles.codeNote}>
              🔑 Requires invite code: <Text style={styles.codeNoteCode}>CRITCASH-FOUNDER</Text>
            </Text>
          ) : null}
        </View>

        {/* =============================================================== */}
        {/* APPAREL SECTION — wearables */}
        {/* =============================================================== */}
        <SectionHeader
          eyebrow="SECTION 01"
          title="Apparel"
          subtitle="Tees, hats, and the limited Founder run."
        />

        {/* =============================================================== */}
        {/* CLASSIC CRITCASH TEE — Phase 1 LIVE */}
        {/* =============================================================== */}
        <View style={styles.productCard}>
          <BrandedHero source={require('../../assets/ClassicTee_Front.webp')} />

          <View style={styles.productGallery}>
            <BrandedGalleryCell source={require('../../assets/ClassicTee_Back.webp')} />
            <BrandedGalleryCell source={require('../../assets/ClassicTee_Sleeve.webp')} />
          </View>

          <Text style={styles.productTitle}>Classic CritCash Tee</Text>
          <View style={styles.priceRow}>
            <Text style={styles.productPrice}>$30</Text>
            <Text style={styles.productPriceSub}>Sizes S–5XL</Text>
          </View>

          <Text style={styles.productDescription}>
            The everyday tee. Broader fit range (S–5XL), no cap on availability.
            Wear the brand without claiming a founder slot.
          </Text>

          {/* Both Buy Now and Add to Cart open the same size modal — the
              modal's onAddToCart callback decides which button the user
              tapped from there. Same pattern for Mouse Pad / Coffee Mug
              / Founder Tee below. */}
          <TouchableOpacity
            style={styles.buyButton}
            onPress={openClassicTeeModal}
          >
            <Text style={styles.buyButtonText}>Buy Now — $30</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.addCartButtonSecondary}
            onPress={openClassicTeeModal}
          >
            <Text style={styles.addCartButtonSecondaryText}>Add to Cart</Text>
          </TouchableOpacity>
        </View>

        {/* =============================================================== */}
        {/* HAT — single variant (direct add, no modal) */}
        {/* =============================================================== */}
        <StandardProductCard
          title="CritCash Hat"
          price="25"
          image={require('../../assets/Hat.webp')}
          description="Embroidered front (not screen-printed — won't crack or peel over time). One size fits most, adjustable strap on the back."
          sizeNote="One size · Adjustable"
          onBuy={() => handleDirectCheckout('hat')}
          loading={directCheckoutInFlight === 'hat'}
          onAddToCart={() =>
            addToCart({
              productKey: 'hat',
              productName: 'CritCash Hat',
              size: 'OS',
              sizeLabel: 'One size',
              unitAmountCents: 2500,
              image: require('../../assets/Hat.webp'),
            })
          }
        />

        {/* =============================================================== */}
        {/* ACCESSORIES SECTION — gear, desk, drinkware */}
        {/* =============================================================== */}
        <SectionHeader
          eyebrow="SECTION 02"
          title="Accessories"
          subtitle="Gear for the desk, the carry, and the mug rotation."
        />

        {/* =============================================================== */}
        {/* BACKPACK — single variant (direct add, no modal) */}
        {/* =============================================================== */}
        <StandardProductCard
          title="CritCash Utility Backpack"
          price="65"
          image={require('../../assets/Backpack.webp')}
          description="Built for daily carry — gear, controllers, laptop, snacks. All-over print means no front-pocket logo to wear out over time."
          sizeNote="All-over print"
          onBuy={() => handleDirectCheckout('backpack')}
          loading={directCheckoutInFlight === 'backpack'}
          onAddToCart={() =>
            addToCart({
              productKey: 'backpack',
              productName: 'CritCash Utility Backpack',
              size: 'OS',
              sizeLabel: 'One size',
              unitAmountCents: 6500,
              image: require('../../assets/Backpack.webp'),
            })
          }
        />

        {/* =============================================================== */}
        {/* MOUSE PAD — 36×18 desk mat only */}
        {/* =============================================================== */}
        <StandardProductCard
          title="King Crit Mouse Pad"
          priceLabel="$30"
          image={require('../../assets/MousePad.webp')}
          description="The full 36″×18″ desk mat — covers your whole setup, keyboard and all."
          sizeNote="36″×18″ desk mat · $30"
          onBuy={openMousePadModal}
          // Modal confirms the single desk-mat variant + hosts Add to Cart.
          onAddToCart={openMousePadModal}
        />

        {/* =============================================================== */}
        {/* COFFEE MUG — size modal (11 oz / 15 oz) */}
        {/* =============================================================== */}
        <StandardProductCard
          title="King Crit Coffee Mug"
          priceLabel="From $20"
          image={require('../../assets/CoffeeMug.webp')}
          description="Standard 11 oz for daily coffee. Or upgrade to 15 oz for the morning that needs more."
          sizeNote="11 oz $20 · 15 oz $25"
          onBuy={openCoffeeMugModal}
          // Coffee Mug needs a size choice → modal routes the Add to Cart
          // path through its own button.
          onAddToCart={openCoffeeMugModal}
        />

        {/* Disclaimer */}
        <View style={styles.disclaimer}>
          <Text style={styles.disclaimerText}>
            US-only fulfillment. Print-on-demand via Printful. Production takes
            2–5 business days, plus shipping (transit time varies by location).
            Most orders arrive within 1–2 weeks. Sizing collected at checkout.
          </Text>
        </View>

        <View style={{ height: 100 }} />
      </ScrollView>

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />

      {/* Size selector modals — one per multi-variant product. Each
          passes onAddToCart so the modal renders a secondary "Add to
          Cart" button alongside its Buy Now / Checkout CTA. The
          callback receives { productKey, size, sizeLabel,
          unitAmountCents, productName } resolved from the selected
          variant; we attach the local image asset here so the cart
          screen can render a thumbnail. */}
      <MerchSizeSelector
        visible={classicTeeModalOpen}
        onClose={() => setClassicTeeModalOpen(false)}
        navigation={navigation}
        productKey="classic_tee"
        productName="Classic CritCash Tee"
        priceCents={3000}
        sizes={CLASSIC_TEE_SIZES}
        onAddToCart={(payload) => addToCart({ ...payload, image: require('../../assets/ClassicTee_Front.webp') })}
      />
      <MerchSizeSelector
        visible={mousePadModalOpen}
        onClose={() => setMousePadModalOpen(false)}
        navigation={navigation}
        productKey="mouse_pad"
        productName="King Crit Mouse Pad"
        priceCents={3000}
        sizes={MOUSE_PAD_SIZES}
        onAddToCart={(payload) => addToCart({ ...payload, image: require('../../assets/MousePad.webp') })}
      />
      <MerchSizeSelector
        visible={coffeeMugModalOpen}
        onClose={() => setCoffeeMugModalOpen(false)}
        navigation={navigation}
        productKey="coffee_mug"
        productName="King Crit Coffee Mug"
        priceCents={2000}
        sizes={COFFEE_MUG_SIZES}
        onAddToCart={(payload) => addToCart({ ...payload, image: require('../../assets/CoffeeMug.webp') })}
      />
      <MerchSizeSelector
        visible={founderTeeModalOpen}
        onClose={() => setFounderTeeModalOpen(false)}
        navigation={navigation}
        productKey="founder_tee"
        productName="Founder Tee · First Blood Edition"
        priceCents={5000}
        sizes={FOUNDER_TEE_SIZES}
        onAddToCart={(payload) => addToCart({ ...payload, image: require('../../assets/FounderTee_Solo.webp') })}
      />
      <FounderCodeModal
        visible={founderCodeModalOpen}
        onClose={() => setFounderCodeModalOpen(false)}
        onRedeem={handleRedeemFounderCode}
      />
    </SafeAreaView>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0A0A0A',
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 20,
  },

  // Hero
  hero: {
    alignItems: 'center',
    paddingVertical: 30,
    borderBottomWidth: 1,
    borderBottomColor: '#222',
    marginBottom: 24,
  },
  eyebrow: {
    color: '#00B4D8',
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 2,
    marginBottom: 8,
  },
  heroTitle: {
    color: '#FFF',
    fontSize: 32,
    fontWeight: '800',
    marginBottom: 8,
    textAlign: 'center',
  },
  heroSubtitle: {
    color: '#999',
    fontSize: 16,
    textAlign: 'center',
    maxWidth: 480,
  },

  // Section header — opens each category group (Apparel / Accessories).
  // Gold underline divider matches the brand palette and visually separates
  // the section from the product cards that follow.
  sectionHeader: {
    alignItems: 'center',
    paddingTop: 8,
    paddingBottom: 16,
    marginBottom: 8,
  },
  sectionEyebrow: {
    color: '#00B4D8',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 3,
    marginBottom: 6,
  },
  sectionTitle: {
    color: '#FFF',
    fontSize: 26,
    fontWeight: '800',
    marginBottom: 10,
  },
  sectionDivider: {
    width: 60,
    height: 2,
    backgroundColor: '#FCD000',
    marginBottom: 10,
  },
  sectionSubtitle: {
    color: '#888',
    fontSize: 13,
    textAlign: 'center',
    maxWidth: 420,
    lineHeight: 18,
  },

  // Product card
  productCard: {
    backgroundColor: '#1A1A1A',
    borderRadius: 16,
    padding: 20,
    marginBottom: 24,
  },
  productCardFeatured: {
    borderWidth: 2,
    borderColor: '#FCD000',
  },
  productBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: '#FCD000',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    gap: 6,
    marginBottom: 16,
  },
  productBadgeText: {
    color: '#0A0A0A',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1,
  },
  productHeroImage: {
    width: '100%',
    aspectRatio: 0.85,
    maxWidth: 500,
    alignSelf: 'center',
    marginBottom: 12,
  },

  // Branded hero — wraps a transparent product PNG over the CritCash
  // background. Subtle gold border visually contains the image inside the
  // dark product card (fixes the "huge gap" feel the standalone transparent
  // PNGs created against the same-tone card background).
  brandedHero: {
    position: 'relative',
    width: '100%',
    aspectRatio: 0.85,
    maxWidth: 500,
    alignSelf: 'center',
    marginBottom: 12,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#FCD00040',
    backgroundColor: '#0E0E0E',
  },
  brandedHeroBg: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
  },
  brandedHeroImg: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
  },
  // Foreground product image — anchored bottom so the scale-up keeps the
  // model's legs at the bottom border instead of trimming them off.
  brandedHeroFg: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    transform: [{ scale: 1.25 }],
    transformOrigin: 'center bottom',
  },
  brandedGalleryCell: {
    flex: 1,
    position: 'relative',
    aspectRatio: 1,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#FCD00040',
    backgroundColor: '#0E0E0E',
  },
  standardHeroImage: {
    width: '100%',
    aspectRatio: 1,
    maxWidth: 360,
    alignSelf: 'center',
    marginBottom: 12,
  },
  productGallery: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 20,
  },
  galleryCell: {
    flex: 1,
    backgroundColor: '#0E0E0E',
    borderRadius: 12,
    padding: 8,
    aspectRatio: 1,
  },
  galleryImage: {
    width: '100%',
    height: '100%',
  },
  productTitle: {
    color: '#FFF',
    fontSize: 22,
    fontWeight: '700',
    marginBottom: 6,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 14,
  },
  productPrice: {
    color: '#FCD000',
    fontSize: 28,
    fontWeight: '800',
  },
  productPriceSub: {
    color: '#888',
    fontSize: 12,
  },
  productDescription: {
    color: '#CCC',
    fontSize: 14,
    lineHeight: 22,
    marginBottom: 20,
  },

  // Included list
  includedList: {
    backgroundColor: '#0E0E0E',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  includedTitle: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 10,
  },
  includedRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 6,
    gap: 10,
  },
  includedText: {
    flex: 1,
    color: '#DDD',
    fontSize: 13,
    lineHeight: 19,
  },

  // Buy button
  buyButton: {
    backgroundColor: '#FCD000',
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
    marginBottom: 10,
  },
  buyButtonDisabled: {
    backgroundColor: '#555',
  },
  // Secondary "Add to Cart" — transparent w/ gold border, sits below the
  // solid Buy Now button on each product card. Flips to success styling for
  // ~1.5s after a successful add so the user gets immediate confirmation.
  addCartButtonSecondary: {
    backgroundColor: 'transparent',
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#FCD000',
    marginBottom: 8,
  },
  addCartButtonAdded: {
    backgroundColor: '#1a2a0a',
    borderColor: '#00FF88',
  },
  addCartButtonSecondaryText: {
    color: '#FCD000',
    fontSize: 14,
    fontWeight: '800',
  },
  addCartButtonAddedText: {
    color: '#00FF88',
  },
  buyButtonSoldOut: {
    backgroundColor: '#3A0E0E',
    borderWidth: 1,
    borderColor: '#FF6B6B',
  },

  // Founder code modal
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  codeModal: {
    width: '100%',
    maxWidth: 440,
    backgroundColor: '#1A1A1A',
    borderRadius: 16,
    padding: 24,
    borderWidth: 1,
    borderColor: '#333',
  },
  codeModalHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  codeModalEyebrow: {
    color: '#00B4D8',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 2,
    marginBottom: 6,
  },
  codeModalTitle: {
    color: '#FFF',
    fontSize: 20,
    fontWeight: '700',
  },
  codeModalBody: {
    color: '#CCC',
    fontSize: 13,
    lineHeight: 20,
    marginBottom: 16,
  },
  codeModalInput: {
    backgroundColor: '#0E0E0E',
    borderWidth: 2,
    borderColor: '#333',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    color: '#FCD000',
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 1,
    marginBottom: 12,
  },
  codeModalError: {
    color: '#FF6B6B',
    fontSize: 13,
    marginBottom: 12,
  },
  confirmButton: {
    backgroundColor: '#FCD000',
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
  },
  confirmButtonDisabled: {
    backgroundColor: '#555',
  },
  confirmButtonText: {
    color: '#0A0A0A',
    fontSize: 16,
    fontWeight: '800',
  },
  buyButtonText: {
    color: '#0A0A0A',
    fontSize: 16,
    fontWeight: '800',
  },
  codeNote: {
    color: '#888',
    fontSize: 12,
    textAlign: 'center',
    marginTop: 4,
  },
  codeNoteCode: {
    color: '#FCD000',
    fontWeight: '700',
    letterSpacing: 1,
  },

  // Disclaimer
  disclaimer: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  disclaimerText: {
    color: '#666',
    fontSize: 12,
    lineHeight: 18,
    textAlign: 'center',
  },
});
